// Seed catalog for the public (client-side) Propulsion Calculator demo.
// Realistic first-order specs — enough for the modeled estimate to be sensible.
// Verified thrust-stand data and the full editable catalog live behind sign-in.
//
// Fields mirror the backend catalog rows the model reads:
//   motor:     kv, no_load_current_a, resistance_ohm, max_current_a
//   propeller: diameter_in, pitch_in, t_const, p_const
//   esc:       resistance_ohm, max_current_a
//   battery:   nominal_voltage_v (per cell), resistance_ohm (per cell),
//              capacity_mah (pack), c_rating_max

export const DEMO_MOTORS = [
  { id: "m-rs2205", brand: "Emax", name: "RS2205", kv: 2300, no_load_current_a: 1.6, resistance_ohm: 0.03, max_current_a: 32, weight_g: 30 },
  { id: "m-f80", brand: "T-Motor", name: "F80 Pro", kv: 1900, no_load_current_a: 1.5, resistance_ohm: 0.025, max_current_a: 46, weight_g: 40 },
  { id: "m-x2212", brand: "SunnySky", name: "X2212", kv: 980, no_load_current_a: 0.5, resistance_ohm: 0.09, max_current_a: 18, weight_g: 56 },
  { id: "m-mn3110", brand: "T-Motor", name: "MN3110", kv: 470, no_load_current_a: 0.5, resistance_ohm: 0.12, max_current_a: 25, weight_g: 80 },
  { id: "m-mn5008", brand: "T-Motor", name: "MN5008", kv: 340, no_load_current_a: 0.4, resistance_ohm: 0.08, max_current_a: 30, weight_g: 118 },
  { id: "m-u8", brand: "T-Motor", name: "U8 Lite", kv: 150, no_load_current_a: 0.3, resistance_ohm: 0.06, max_current_a: 40, weight_g: 240 },
];

export const DEMO_PROPS = [
  { id: "p-hq5", brand: "HQProp", name: "5×4.3×3", diameter_in: 5, pitch_in: 4.3, blades: 3, t_const: 1.0, p_const: 1.05 },
  { id: "p-gf6", brand: "Gemfan", name: "6×4×3", diameter_in: 6, pitch_in: 4, blades: 3, t_const: 1.0, p_const: 1.0 },
  { id: "p-apc10", brand: "APC", name: "10×4.7", diameter_in: 10, pitch_in: 4.7, blades: 2, t_const: 0.95, p_const: 1.0 },
  { id: "p-dji9450", brand: "DJI", name: "9450", diameter_in: 9.4, pitch_in: 5, blades: 2, t_const: 1.0, p_const: 1.0 },
  { id: "p-tm15", brand: "T-Motor", name: "15×5 CF", diameter_in: 15, pitch_in: 5, blades: 2, t_const: 1.05, p_const: 1.05 },
  { id: "p-tm18", brand: "T-Motor", name: "18×6.1 CF", diameter_in: 18, pitch_in: 6.1, blades: 2, t_const: 1.05, p_const: 1.1 },
];

export const DEMO_ESCS = [
  { id: "e-blheli45", brand: "Generic", name: "BLHeli_32 45A", resistance_ohm: 0.006, max_current_a: 45 },
  { id: "e-f55", brand: "T-Motor", name: "F55A Pro II", resistance_ohm: 0.005, max_current_a: 55 },
  { id: "e-xrotor40", brand: "Hobbywing", name: "XRotor 40A", resistance_ohm: 0.007, max_current_a: 40 },
  { id: "e-alpha60", brand: "T-Motor", name: "Alpha 60A", resistance_ohm: 0.004, max_current_a: 60 },
  { id: "e-flame80", brand: "T-Motor", name: "FLAME 80A", resistance_ohm: 0.003, max_current_a: 80 },
];

export const DEMO_BATTERIES = [
  { id: "b-1500", brand: "Tattu", name: "R-Line 1500mAh 100C", capacity_mah: 1500, nominal_voltage_v: 3.7, resistance_ohm: 0.003, c_rating_max: 100 },
  { id: "b-2200", brand: "Generic", name: "LiPo 2200mAh 45C", capacity_mah: 2200, nominal_voltage_v: 3.7, resistance_ohm: 0.004, c_rating_max: 45 },
  { id: "b-4000", brand: "Generic", name: "LiPo 4000mAh 30C", capacity_mah: 4000, nominal_voltage_v: 3.7, resistance_ohm: 0.0035, c_rating_max: 30 },
  { id: "b-5200", brand: "Generic", name: "LiPo 5200mAh 25C", capacity_mah: 5200, nominal_voltage_v: 3.7, resistance_ohm: 0.003, c_rating_max: 25 },
  { id: "b-liion", brand: "Molicel", name: "P42A Li-ion 3500mAh", capacity_mah: 3500, nominal_voltage_v: 3.6, resistance_ohm: 0.012, c_rating_max: 12 },
];

// Drone profiles — pre-fill the airframe (and a sensible motor) so a visitor can
// pick a profile and calculate in two clicks. Prop/ESC/battery stay a choice.
export const DEMO_DRONES = [
  { id: "d-5fpv", brand: "5-inch", name: "FPV Freestyle", drone_class: "5-inch FPV", auw_g: 650, num_motors: 4, cells_series: 4, coaxial: false, motors: [{ id: "m-rs2205" }] },
  { id: "d-450", brand: "Quad", name: "450 Class", drone_class: "450 quad", auw_g: 1200, num_motors: 4, cells_series: 3, coaxial: false, motors: [{ id: "m-x2212" }] },
  { id: "d-hexa", brand: "Hexa", name: "Survey Hex", drone_class: "Hexacopter", auw_g: 3800, num_motors: 6, cells_series: 6, coaxial: false, motors: [{ id: "m-mn5008" }] },
  { id: "d-x8", brand: "X8", name: "Cinelifter", drone_class: "Coaxial X8", auw_g: 6500, num_motors: 8, cells_series: 6, coaxial: true, motors: [{ id: "m-u8" }] },

  // DJI profiles. Propulsion is integrated (motors/props aren't user-swappable
  // parts), so these pre-fill the airframe only — all-up weight, motor count and
  // cell count — and leave the power system for you to size against. AUW is the
  // ready-to-fly weight incl. the stock intelligent flight battery.
  { id: "d-dji-mini4", brand: "DJI", name: "Mini 4 Pro", drone_class: "Sub-250 g", auw_g: 249, num_motors: 4, cells_series: 2, coaxial: false },
  { id: "d-dji-avata2", brand: "DJI", name: "Avata 2", drone_class: "Cinewhoop FPV", auw_g: 377, num_motors: 4, cells_series: 4, coaxial: false },
  { id: "d-dji-air3", brand: "DJI", name: "Air 3", drone_class: "Folding quad", auw_g: 720, num_motors: 4, cells_series: 4, coaxial: false },
  { id: "d-dji-mavic3", brand: "DJI", name: "Mavic 3", drone_class: "Folding quad", auw_g: 895, num_motors: 4, cells_series: 4, coaxial: false },
  { id: "d-dji-p4p", brand: "DJI", name: "Phantom 4 Pro V2", drone_class: "Prosumer quad", auw_g: 1388, num_motors: 4, cells_series: 4, coaxial: false },
  { id: "d-dji-m350", brand: "DJI", name: "Matrice 350 RTK", drone_class: "Enterprise quad", auw_g: 6470, num_motors: 4, cells_series: 12, coaxial: false },
];

// Flight controllers — the section eCalc doesn't have. Draw in watts (whole
// stack incl. IMU/baro), weight in grams, operating range in °C (approx from
// published specs).
export const DEMO_FCUS = [
  { id: "fc-pixhawk6c", brand: "Holybro", name: "Pixhawk 6C", power_draw_w: 3.0, weight_g: 35, temp_min_c: -20, temp_max_c: 60 },
  { id: "fc-cubeorange", brand: "CubePilot", name: "Cube Orange+", power_draw_w: 4.0, weight_g: 73, temp_min_c: -10, temp_max_c: 60 },
  { id: "fc-speedybee", brand: "SpeedyBee", name: "F405 V4", power_draw_w: 1.5, weight_g: 10, temp_min_c: -10, temp_max_c: 50 },
  { id: "fc-kakuteh7", brand: "Holybro", name: "Kakute H7", power_draw_w: 1.5, weight_g: 8, temp_min_c: -10, temp_max_c: 50 },
];

// Sensors / payloads — multi-selectable; draw + weight add to the system, temp
// range is checked against the operating environment.
export const DEMO_SENSORS = [
  { id: "s-m9n", brand: "u-blox", name: "M9N GPS", sensor_type: "GPS", power_draw_w: 0.35, weight_g: 32, temp_min_c: -40, temp_max_c: 85 },
  { id: "s-tfmini", brand: "Benewake", name: "TFmini-S LiDAR", sensor_type: "Rangefinder", power_draw_w: 0.6, weight_g: 5, temp_min_c: -20, temp_max_c: 60 },
  { id: "s-oakd", brand: "Luxonis", name: "OAK-D Lite depth cam", sensor_type: "Camera", power_draw_w: 4.5, weight_g: 61, temp_min_c: -20, temp_max_c: 50 },
  { id: "s-fpv", brand: "Caddx", name: "Ratel 2 FPV cam", sensor_type: "Camera", power_draw_w: 0.7, weight_g: 9, temp_min_c: -20, temp_max_c: 60 },
  { id: "s-sik", brand: "Holybro", name: "SiK telemetry 915", sensor_type: "Telemetry", power_draw_w: 1.0, weight_g: 20, temp_min_c: -40, temp_max_c: 85 },
  { id: "s-flow", brand: "PixArt", name: "PMW3901 optical flow", sensor_type: "Optical flow", power_draw_w: 0.15, weight_g: 3, temp_min_c: -20, temp_max_c: 70 },
];

export const DEMO_CATALOG = {
  motors: DEMO_MOTORS,
  propellers: DEMO_PROPS,
  escs: DEMO_ESCS,
  batteries: DEMO_BATTERIES,
  fcus: DEMO_FCUS,
  sensors: DEMO_SENSORS,
};
